import React, { useEffect, useState } from 'react'
import { Box, IconButton, MenuItem } from '@mui/material'
import { UserStatusEnum, useUserStatusOptions } from '@data/users/user'
import { useRouteQuery, useTranslator } from '@/hooks'
import { InputField } from '../common'

interface UserStatusSelectionProps {
  value: string
  onSelect?: (status: UserStatusEnum) => void
  disabled?: boolean
  required?: boolean
  label?: string
  className?: string
  fullWidth?: boolean
  placeholder?: string
  excludeGuestStatuses?: boolean
  isTransaction?: boolean
}

const UserStatusSelection = ({
  onSelect,
  disabled = false,
  required = false,
  label = '',
  className = '',
  fullWidth = true,
  placeholder = '',
  excludeGuestStatuses = false,
  isTransaction = false
}: UserStatusSelectionProps) => {
  const { __ } = useTranslator()

  const statusOptions = useUserStatusOptions()

  const { searchQuery } = useRouteQuery()
  const [value, setValue] = useState<string>()

  useEffect(() => {
    const status = searchQuery.get('status') || ''

    setValue(status)
  }, [searchQuery])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value as UserStatusEnum

    onSelect?.(selectedValue)
  }

  const handleClear = () => {
    setValue('')
    onSelect?.(undefined as any)
  }

  const baseOptions = isTransaction
    ? statusOptions.filter(option =>
        [UserStatusEnum.WAITING_DELETE, UserStatusEnum.WAITING, UserStatusEnum.ACTIVE, UserStatusEnum.DISABLE, UserStatusEnum.BLOCKED].includes(
          option.value as UserStatusEnum
        )
      )
    : statusOptions

  const filteredStatusOptions = excludeGuestStatuses
    ? baseOptions.filter(
        option => ![UserStatusEnum.GUEST, UserStatusEnum.GUEST_WAITING].includes(option.value as UserStatusEnum)
      )
    : baseOptions

  return (
    <InputField
      select
      fullWidth={fullWidth}
      label={label}
      value={value || ''}
      onChange={handleChange}
      disabled={disabled}
      required={required}
      className={className}
      placeholder={placeholder}
      slotProps={{
        input: {
          endAdornment: value ? (
            <Box mr={5}>
              <IconButton size='small' onClick={handleClear} aria-label='clear selection' tabIndex={-1}>
                <i className='tabler-x text-[16px] text-textPrimary'></i>
              </IconButton>
            </Box>
          ) : null
        },
        select: {
          displayEmpty: true,
          renderValue: (selected: any) => {
            if (!selected) {
              return <span style={{ color: '#aaa' }}>{placeholder || label || __('Select status')}</span>
            }

            const found = statusOptions.find(option => option.value === selected)

            return found ? found.label : selected
          }
        }
      }}
      sx={{
        '& .MuiSelect-select': {
          textAlign: 'left'
        },
        '& .MuiInputBase-root': {
          height: '38px'
        }
      }}
    >
      <MenuItem value=''>{placeholder || 'All statuses'}</MenuItem>
      {filteredStatusOptions.map(option => (
        <MenuItem key={option.value || '123'} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </InputField>
  )
}

export default UserStatusSelection
